// Print-friendly PDF helper for analysis reports (Partners, Surveys,
// Customer Lab, …). Opens a NEW WINDOW with a clean Arabic RTL layout
// then triggers `window.print()`. The user can either print on paper
// or "Save as PDF" via the browser's print dialog — no backend PDF
// library required, perfect Arabic rendering, works in all browsers.

import { formatKSADateTime } from "./ksaTime";

export function openPrintReport({
  title = "تقرير",
  subtitle = "",
  generatedAt = null,
  stats = [],          // [{ label, value, hint? }]
  topRows = [],        // [{ name, code?, value, sub? }]
  sections = [],       // [{ heading, body, kind? }]   kind: "text" | "list"
  footer = "ثواني · Thawany",
} = {}) {
  const w = window.open("", "_blank", "width=1024,height=768");
  if (!w) {
    alert("يرجى السماح بالنوافذ المنبثقة لطباعة التقرير");
    return;
  }
  const fmtDate = generatedAt
    ? formatKSADateTime(new Date(generatedAt), { dateStyle: "long", timeStyle: "short" })
    : formatKSADateTime(new Date(), { dateStyle: "long", timeStyle: "short" });

  const html = `<!doctype html>
<html dir="rtl" lang="ar">
<head>
<meta charset="utf-8">
<title>${escape(title)} — ${fmtDate}</title>
<style>
  @page { size: A4; margin: 16mm 14mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: -apple-system, "Segoe UI", "Tahoma", Arial, sans-serif; color: #1f2937; background: #fff; line-height: 1.8; }
  body { padding: 24px; }
  h1 { margin: 0; font-size: 26px; color: #4C1D95; letter-spacing: -0.3px; }
  h2 { margin: 26px 0 10px; font-size: 17px; color: #4C1D95; border-bottom: 2px solid #DDD6FE; padding-bottom: 4px; }
  .meta { color: #6b7280; font-size: 12px; margin-top: 4px; }
  .subtitle { color: #6b7280; font-size: 13px; margin-top: 6px; }
  .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 16px; }
  .stat { padding: 10px 12px; background: #F5F3FF; border-inline-start: 3px solid #7C3AED; border-radius: 6px; }
  .stat .lbl { font-size: 10.5px; color: #6b7280; }
  .stat .val { font-size: 18px; font-weight: 800; color: #1f2937; margin-top: 2px; }
  .stat .hint { font-size: 10.5px; color: #9ca3af; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px; }
  th, td { padding: 7px 8px; border-bottom: 1px solid #e5e7eb; text-align: start; }
  thead th { background: #F5F3FF; color: #4C1D95; font-weight: 700; font-size: 11px; }
  tr:nth-child(even) td { background: #FAFAFA; }
  .section-body { font-size: 13px; white-space: pre-wrap; line-height: 1.85; padding: 6px 0; }
  .section-body.list { white-space: normal; }
  .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 10.5px; color: #9ca3af; text-align: center; }
  .actions { position: fixed; top: 14px; inset-inline-end: 14px; display: flex; gap: 6px; }
  .actions button { padding: 8px 14px; background: #4C1D95; color: #fff; border: none; border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer; }
  .actions .secondary { background: #fff; color: #4C1D95; border: 1px solid #4C1D95; }
  @media print { .actions { display: none; } body { padding: 0; } }
</style>
</head>
<body>
  <div class="actions">
    <button class="secondary" onclick="window.close()">إغلاق</button>
    <button onclick="window.print()">🖨️ طباعة / حفظ PDF</button>
  </div>

  <h1>${escape(title)}</h1>
  ${subtitle ? `<div class="subtitle">${escape(subtitle)}</div>` : ""}
  <div class="meta">📅 تاريخ التوليد: ${escape(fmtDate)}</div>

  ${stats.length ? `
    <h2>📊 ملخص الأرقام</h2>
    <div class="stats">
      ${stats.map((s) => `
        <div class="stat">
          <div class="lbl">${escape(s.label || "")}</div>
          <div class="val">${escape(formatVal(s.value))}</div>
          ${s.hint ? `<div class="hint">${escape(s.hint)}</div>` : ""}
        </div>`).join("")}
    </div>
  ` : ""}

  ${topRows.length ? `
    <h2>🏆 أبرز الشركاء</h2>
    <table>
      <thead><tr>
        <th>#</th><th>الاسم</th><th>الكود</th><th>القيمة</th><th></th>
      </tr></thead>
      <tbody>
        ${topRows.map((r, i) => `
          <tr>
            <td>${i + 1}</td>
            <td>${escape(r.name || "—")}</td>
            <td>${escape(r.code || "—")}</td>
            <td><b>${escape(formatVal(r.value))}</b></td>
            <td style="color:#6b7280;font-size:11px;">${escape(r.sub || "")}</td>
          </tr>
        `).join("")}
      </tbody>
    </table>
  ` : ""}

  ${sections.map((s) => `
    <h2>${escape(s.heading || "")}</h2>
    <div class="section-body ${s.kind === "list" ? "list" : ""}">${
      s.kind === "list" && Array.isArray(s.body)
        ? "<ul>" + s.body.map((b) => `<li>${escape(b)}</li>`).join("") + "</ul>"
        : escape(s.body || "—")
    }</div>
  `).join("")}

  <div class="footer">${escape(footer)} · ${escape(fmtDate)}</div>

  <script>
    // Auto-open print dialog so the user lands directly on Print/PDF.
    setTimeout(() => { try { window.focus(); window.print(); } catch (e) {} }, 350);
  </script>
</body>
</html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();
}

function escape(v) {
  return String(v == null ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatVal(v) {
  if (v == null) return "—";
  if (typeof v === "number") return v.toLocaleString("ar-EG-u-nu-latn");
  return v;
}
